<template>
  <div class="seat-selection-page" v-if="Object.keys(this.booking_data).length">
    <!-- nel js c'è un router push a 'detail' se non trovo booking_data (es. se refresho la pagina checkout). questo ulteriore controllo è per evitare un undefined rischioso a template -->
    <b-container v-if="!loading" class="my-4">
      <b-row>
        <b-col class="ml-3">
          <button-back />
        </b-col>
      </b-row>
    </b-container>
    <b-container>
      <section v-if="loading" class="section section--loader">
        <loader size="large" :show_timing_slot="true" />
      </section>
      <b-row>
        <b-col sm="12" md="12" lg="8" xl="9">
          <div
            v-if="!loading && Object.keys(mappedDecks).length > 0"
            class="seat-map-frame"
          >
            <seat-map
              :mustSelectSeats="mustSelectSeats"
              :mappedDecks="mappedDecks"
              :passengers="passengers"
              :selections="selectedSeats"
              :handleSeatSelection="
                (segmentId, passengerId, seat) =>
                  handleSeatSelection(segmentId, passengerId, seat)
              "
              :resetSeatSelection="() => resetSeatSelection()"
              :updateParentWithCurrentStatus="
                (data) => updateParentWithCurrentStatus(data)
              "
              :resetSegmentSelections="
                (segmentId) => resetSegmentSelections(segmentId)
              "
              @tracked_event="handle_tracked_event"
            />
          </div>
        </b-col>
        <b-col sm="12" md="12" lg="4" xl="3">
          <div v-if="!loading" class="sidebar sticky-top">
            <div class="detail--panel detail--panel-sidebar pt-0" compact>
              <sidebar-resume :flight_offers="[flight_offer]" />
              <btn-checkout
                @handle_data="handleNextStep()"
                :conditions="
                  btnDisabled ||
                  get_availability(get_markup(flight_offer).mu_price)
                    .show_credit_alert
                "
                :popover="
                  get_availability(get_markup(flight_offer).mu_price)
                    .show_credit_alert
                    ? ''
                    : checkout_btn_popover
                "
                id="btn-checkout-sidebar"
                :copy="btnText"
              />
              <p
                v-if="
                  get_availability(get_markup(flight_offer).mu_price)
                    .show_credit_alert
                "
                v-b-popover.hover.bottom="
                  'Il tuo credito welfare non è sufficiente ad acquistare questa soluzione'
                "
                class="credit-warning text-center text-danger mt-2 mb-1"
              >
                <vb-icon
                  name="info"
                  class="text-danger"
                  :color="sass('danger')"
                  :size="16"
                />
                Credito insufficiente
              </p>
              <p
                v-else-if="btnDisabled"
                class="credit-warning text-center text-info mt-2 mb-1"
              >
                Seleziona tutti i posti di ogni passeggero prima di continuare.
              </p>
            </div>
          </div>
        </b-col>
      </b-row>
    </b-container>
    <vb-modal
      :vb_options="{
        id: 'seat-selection-fail-modal',
        title: 'Posto non selezionabile',
        ok_only: true,
        centered: true,
        ok_variant: 'info',
      }"
    >
      {{ modalDescription }}
    </vb-modal>
  </div>
</template>

<script>
import { get_config, rybbit_event } from "@/utilities/";
import { parse_flight_offers_date } from "@liveflight/utilities/dates";
import { reverse_date, get_availability } from "@/utilities/commons";
import { service_handler } from "@/services";
import SidebarResume from "@liveflight/components/blocks/resume/resume";
import BtnCheckout from "@liveflight/components/atoms/btn-checkout";
import Loader from "@components/blocks/loader";
import SeatMap from "@liveflight/components/blocks/seat-map/seat-map.vue";
import {
  addSelectedSeatsToFlightOffer,
  get_flight_offer_dates,
  get_markup,
  get_flight_order_body,
  getSegmentSelectedSeatsFromFlightOffer,
} from "@liveflight/utilities/flight-offer";
import {
  slim_seat,
  slim_selections,
  slim_traveler,
  slim_flight_offer,
} from "@liveflight/utilities/rybbit";
import { SEAT_SUITABLE_FOR_INFANT } from "@liveflight/resources/seat-map";
import { fakeSeatMap } from "@liveflight/utilities/faker";
import {
  filterHeldPassengers,
  mapSeatObjectsToGrid,
  isSeatAvailableForPassenger,
  validate_traveler_pricing,
  validate_deck_configuration,
} from "@liveflight/utilities/seat-map";

export default {
  name: "seat-selection",
  components: {
    "sidebar-resume": SidebarResume,
    "seat-map": SeatMap,
    "btn-checkout": BtnCheckout,
    loader: Loader,
  },
  props: {},
  data() {
    return {
      loading: true,
      reloadSeatMap: false,
      checkout_btn_popover:
        "Prima devi compilare i riferimenti di contatto, i dettagli dei viaggiatori e accettare le condizioni d'uso in questa pagina.",
      seatMap: undefined,
      selectedSeats: JSON.stringify({}),
      mustSelectSeats: false,
      modalDescription: "",
      sittingPassengers: [],
      travelersByAge: {},
      travelersMap: {},
      isEditing: true,
      btnText: "",
      btnDisabled: false,
      seatChains: {},
      mappedDecks: {},
    };
  },
  computed: {
    dictionaries() {
      return this.$store.state.liveflight.results.dictionaries || {};
    },
    heldPassengers() {
      const held = {};
      this.flight_offer.travelerPricings
        .filter((p) => p.travelerType === "HELD_INFANT")
        .forEach((i) => {
          held[i.associatedAdultId] = i;
        });
      return held;
    },
    travelerDetails: {
      get() {
        return this.$store.state.liveflight.traveler_details;
      },
      set(value) {
        this.$store.commit("liveflight/traveler_details", value);
      },
    },
    passengers() {
      const passengers = this.travelerDetails;
      return passengers.map((p) => {
        const heldByPassengerId = this.heldPassengers[p.id];
        return !heldByPassengerId ? p : { ...p, holding: heldByPassengerId };
      });
    },
    parsedSeatSelections: {
      get() {
        try {
          const parsed = JSON.parse(this.selectedSeats);
          return parsed;
        } catch (e) {
          this.log("parsedSeatSelections error");
          this.log(e);
          return {};
        }
      },
      set(value) {
        this.selectedSeats = JSON.stringify(value);
      },
    },
    booking_data: {
      get() {
        return this.$store.state.booking_data;
      },
      set(value) {
        this.$store.commit("booking_data", value);
      },
    },
    flight_offer: {
      get() {
        return this.$store.state.liveflight.flight_offer;
      },
      set(value) {
        this.$store.commit("liveflight/flight_offer", value);
      },
    },
    isNotAffordable() {
      return this.get_availability(this.get_markup(this.flight_offer).mu_price)
        .show_credit_alert;
    },
    hasToSelectAllPassengers() {
      if (!this.mustSelectSeats) return false;

      return this.flight_offer.itineraries.some((itinerary) => {
        const segments = itinerary.segments;
        if (!segments) return false;

        return segments.some(
          (segment) =>
            !this.parsedSeatSelections[segment.id] ||
            Object.keys(this.parsedSeatSelections[segment.id]).length <
              this.sittingPassengers.length,
        );
      });
    },
    confirmDisabled() {
      return this.isNotAffordable || this.hasToSelectAllPassengers;
    },
    contactDetails: {
      get() {
        return this.$store.state.liveflight.contact_details;
      },
      set(value) {
        this.$store.commit("liveflight/contact_details", value);
      },
    },
    flight_offer_rybbit_data() {
      return slim_flight_offer(this.flight_offer)
    },
  },
  created() {
    this.reverse_date = reverse_date;
    this.parse_flight_offers_date = parse_flight_offers_date;
    this.get_availability = get_availability;
    this.get_flight_offer_dates = get_flight_offer_dates;

    this.sittingPassengers = this.filterHeldPassengers(this.passengers);
    let travelersMap = {};
    ["ADULT", "CHILD"].forEach((age) => {
      const passengersByAge = this.passengers.filter(
        (p) => (p.travelerType || p.traveler_type) === age,
      );
      const passengers = Object.fromEntries(
        passengersByAge.map((p) => [p.id, p]),
      );
      this.travelersByAge[age] = {
        count: passengersByAge.length,
        passengers,
      };

      travelersMap = {
        ...travelersMap,
        ...passengers,
      };
    });

    this.travelersMap = travelersMap;
    // se non trovo booking data rimando al dettaglio
    if (!Object.keys(this.booking_data).length) {
      this.$router.replace("detail");
      return;
    }
  },
  mounted() {
    // Only on RyanAir non basic fares
    this.mustSelectSeats = this.checkIfUsersMustSelectSeats();
    if (process && process.env && process.env.VUE_APP_FORCE_SEAT_SELECTION) {
      this.mustSelectSeats = true;
    }

    if (this.mustSelectSeats) {
      this.handle_tracked_event({
        event: "seat-map:mandatory-selection",
        data: {
          type: "info",
        },
      });
    }


    // Fetch seat map
    this.fetchSeatMap();
  },
  watch: {
    reloadSeatMap: {
      handler(newValue, oldValue) {
        (newValue, oldValue);
        const _this = this;
        this.log("reloadSeatMap");
        this.$nextTick(() => {
          _this.fetchSeatMap();
        });
      },
    },
  },
  methods: {
    addSelectedSeatsToFlightOffer,
    getSegmentSelectedSeatsFromFlightOffer,
    mapSeatObjectsToGrid,
    filterHeldPassengers,
    get_markup,
    get_flight_order_body,
    isSeatAvailableForPassenger,
    fetchSeatMap() {
      service_handler("seatmaps", { flightOffer: this.flight_offer })
        .then((response) => {
          try {
            let has_valid_traveler_pricing = false;
            if (response.data && response.data.seatmaps) {
              this.seatMap = response.data.seatmaps;

              // ITSM-513: check seatmap if any traveler is missing pricing data entirely
              has_valid_traveler_pricing = validate_traveler_pricing(
                this.flight_offer,
                this.seatMap.data,
              );

              if (has_valid_traveler_pricing) {
                this.mappedDecks = this.buildMappedDecks(
                  response.data.seatmaps,
                );
              }
            }

            if (this.mustSelectSeats) {
              const error_message =
                "Questa tariffa ha un problema con la mappa dei posti. Per proseguire, ti consigliamo di selezionarne un’altra.";
              // If we must select the seats we have no map: error out back to detail.
              if (!response || this.isSeatMapEmpty(this.seatMap.data)) {
                this.handle_tracked_event({
                  event: "seat-map:mandatory-selection-impossible",
                  data: {
                    type: "error",
                    message: error_message,
                    reason: "Missing seat map data",
                  },
                });
                this.exitWithGlobalModal(error_message, () =>
                  this.$router.replace("detail"),
                );
                return;
              }

              if (!has_valid_traveler_pricing) {
                this.handle_tracked_event({
                  event: "seat-map:mandatory-selection-impossible",
                  data: {
                    type: "error",
                    message: error_message,
                    reason: "Incomplete traveler pricing",
                  },
                });
                this.exitWithGlobalModal(error_message, () =>
                  this.$router.replace("detail"),
                );
                return;
              }

              // If we must select the seats but a user can't: error out back to detail to go back to another possible fare
              if (!this.canAllPassengersSelectOneSeat(this.seatMap.data)) {
                this.handle_tracked_event({
                  event: "seat-map:mandatory-selection-impossible",
                  data: {
                    type: "error",
                    message: error_message,
                    reason: "Some travelers cannot choose a seat in some decks",
                  },
                });
                this.exitWithGlobalModal(error_message, () =>
                  this.$router.replace("detail"),
                );
                return;
              }

              // If we have disabled decks we error out
              const anyDisabled = Object.values({ ...this.mappedDecks }).reduce(
                (res, elem) => res || elem.segment.disabled,
                false,
              );
              if (anyDisabled) {
                this.handle_tracked_event({
                  event: "seat-map:mandatory-selection-impossible",
                  data: {
                    type: "error",
                    message: error_message,
                    reason: "Some decks are disabled",
                  },
                });
                this.exitWithGlobalModal(error_message, () =>
                  this.$router.replace("detail"),
                );
                return;
              }
            }

            // No seats, but also optional, so just go to checkout
            if (!response || this.isSeatMapEmpty(this.seatMap.data)) {
              this.handle_tracked_event({
                event: "seat-map:unavailable",
                data: {
                  type: "info",
                  reason: "Missing seat map",
                },
              });
              this.$router.replace("checkout");
              return;
            }

            // We have the data, but not all the prices needed skip to checkout
            if (!has_valid_traveler_pricing) {
              this.handle_tracked_event({
                event: "seat-map:unavailable",
                data: {
                  type: "info",
                  reason: "Incomplete traveler pricing",
                },
              });
              this.$router.replace("checkout");
              return;
            }

            // If all decks are disabled just go to checkout the user can't book any seat
            const allDisabled = Object.values({ ...this.mappedDecks }).every(
              (elem) => elem.segment.disabled,
            );
            if (allDisabled) {
              this.handle_tracked_event({
                event: "seat-map:unavailable",
                data: {
                  type: "info",
                  reason: "All decks are disabled",
                },
              });
              this.$router.replace("checkout");
              return;
            }

            // Everything is alright so parse previous selections
            this.parsedSeatSelections = this.seatMap.data.reduce(
              (map, segment) => {
                const segmentId = segment.segmentId;
                const segmentSeats = segment.decks.flatMap(
                  (deck) => deck.seats,
                );
                const segmentSelections =
                  this.getSegmentSelectedSeatsFromFlightOffer(
                    this.flight_offer,
                    segmentId,
                    segmentSeats,
                  );
                return {
                  ...map,
                  [segmentId]: segmentSelections,
                };
              },
              {},
            );
          } catch (e) {
            this.log("mounted then");
            this.log(e);
          }
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
          if (process.env.VUE_APP_USE_FAKE_SEAT_MAP) {
            this.seatMap = fakeSeatMap(
              this.flight_offer.itineraries.flatMap((i) => i.segments),
              this.passengers.map((p) => p.id),
            );
            return;
          } else {
            if (this.mustSelectSeats) {
              this.handle_tracked_event({
                event: "seat-map:mandatory-selection-impossible",
                data: {
                  type: "error",
                  reason: "Error from APIs",
                },
              });
              // We had an error go back to detail
              this.$router.replace("detail");
            }
            // If seats are optional just go to checkout
            this.$router.replace("checkout");
          }
        });
    },
    adjustAvailableSeatsCountersForSegment(segment, mappedDeck) {
      const { availableSeatsRemoved = {} } = mappedDeck;
      return (segment.availableSeatsCounters || []).map((seatCounter) => ({
        ...seatCounter,
        value:
          seatCounter.value -
          (availableSeatsRemoved[seatCounter.travelerId] || 0),
      }));
    },
    buildMappedDecks(seatMap) {
      if (!seatMap || !this.flight_offer) {
        return {};
      }

      const mappedDecks = {};
      const areInfantsPresent = Object.keys(this.heldPassengers).length > 0;

      const allSegments = (this.flight_offer.itineraries || []).flatMap(
        (itinerary) => itinerary.segments || [],
      );
      allSegments.forEach((segment) => {
        let segmentSeatsData = segment;
        // Default to at least one deck disabled
        const segmentDecks = {
          0: {
            disabled: true,
          },
        };
        const segmentSeatsDataIndex = (seatMap.data || []).findIndex(
          (e) => e.segmentId === segment.id,
        );
        if (segmentSeatsDataIndex >= 0) {
          segmentSeatsData = seatMap.data[segmentSeatsDataIndex];
          if (
            this.canAllPassengersSelectOneSeatOnTheSegment(segmentSeatsData)
          ) {
            segmentSeatsData.decks.forEach((deck, i) => {
              // ITSM-513: Skip deck if deckConfiguration doesn't match seat layout
              if (
                !validate_deck_configuration(
                  deck.deckConfiguration,
                  deck.seats,
                ) &&
                !get_config(
                  "guiSettings.customizations.allowMalformedDecks",
                  false,
                )
              ) {
                this.log(
                  "Deck skipped: malformed deckConfiguration (ITSM-513)",
                );
                segmentDecks[i] = { disabled: true };
                return;
              }

              const mappedDeck =
                this.mapSeatObjectsToGrid(
                  this.flight_offer,
                  {
                    ...deck,
                    carrierCode: segmentSeatsData.carrierCode,
                  },
                  seatMap.dictionaries || {},
                ) || {};
              const areSeatsAvailableForAllPassengers =
                this.canAllPassengersSelectOneSeatOnTheSegment({
                  ...segmentSeatsData,
                  availableSeatsCounters:
                    this.adjustAvailableSeatsCountersForSegment(
                      segmentSeatsData,
                      mappedDeck,
                    ),
                });
              const isObjectEmpty = !Object.keys(mappedDeck).length;
              const noSelectableSeatsForInfants =
                !mappedDeck.hasSeatsSuitableForInfant && areInfantsPresent;
              segmentDecks[i] = {
                ...mappedDeck,
                disabled:
                  isObjectEmpty ||
                  noSelectableSeatsForInfants ||
                  !areSeatsAvailableForAllPassengers,
              };
            });
          }
        }
        const segmentId = segmentSeatsData.segmentId || segmentSeatsData.id;
        mappedDecks[segmentId] = {
          decks: segmentDecks,
          segment: {
            ...segmentSeatsData,
            segmentId,
            decks: segmentSeatsData.decks || [{ disabled: true }],
            disabled: Object.values(segmentDecks).every(
              (deck) => deck.disabled,
            ),
          },
        };
      });
      return mappedDecks;
    },
    canAllPassengersSelectOneSeatOnTheSegment(segment) {
      const availableSeatsCounters = segment.availableSeatsCounters || [];
      return (
        availableSeatsCounters.length > 0 &&
        availableSeatsCounters.every((seatCounter) => seatCounter.value > 0)
      );
    },
    canAllPassengersSelectOneSeat(seatMap) {
      return (
        !this.isSeatMapEmpty(seatMap) &&
        seatMap.every((segment) =>
          this.canAllPassengersSelectOneSeatOnTheSegment(segment),
        )
      );
    },
    isSeatMapEmpty(seatMap) {
      return !seatMap || !seatMap.length;
    },
    checkIfUsersMustSelectSeats() {
      try {
        const hasRyanAirSegment = this.flight_offer.itineraries.some(
          (itinerary) => {
            if (!itinerary.segments) return false;
            return itinerary.segments.some((segment) =>
              ["FR", "RR", "RK"].includes(segment.carrierCode),
            );
          },
        );
        return (
          hasRyanAirSegment &&
          this.flight_offer.travelerPricings.some((pricing) =>
            pricing.fareDetailsBySegment.some(
              (fare) =>
                !fare.brandedFare ||
                !["FRValue", "BASIC"].includes(fare.brandedFare),
            ),
          )
        );
      } catch (e) {
        this.log("checkIfUsersMustSelectSeats error");
        this.log(e);
        return false;
      }
    },
    resetSegmentSelections(segmentId) {
      if (this.parsedSeatSelections[segmentId]) {
        const parsedSelections = { ...this.parsedSeatSelections };
        delete parsedSelections[segmentId];
        this.parsedSeatSelections = { ...parsedSelections };
      }
    },
    resetSeatSelection() {
      this.parsedSeatSelections = {};
      // this.updateTravelerDetailsWithSeats({})
    },
    isPassengerHoldingInfant(passengerId) {
      return !!this.heldPassengers[passengerId];
    },
    failSelectionWithReason(params) {
      const { message, traveler_id } = params;
      this.handle_tracked_event({
        event: "seat-map:selection:fail",
        data: {
          type: "warning",
          message,
          traveler_id,
        },
      });

      this.modalDescription = message;
      this.$bvModal.show('seat-selection-fail-modal');
      return false;
    },
    updateTravelerDetailsWithSeats(allSelectedSeats) {
      this.travelerDetails = this.travelerDetails.map((details) => {
        const passengerSeats = [];
        Object.keys(allSelectedSeats).forEach((segmentId) => {
          const passengerId = details.id;
          if (allSelectedSeats[segmentId]) {
            const seatInfo = allSelectedSeats[segmentId][passengerId];
            if (seatInfo) {
              passengerSeats.push({ ...seatInfo, segmentId, passengerId });
            }
          }
        });
        const updated = {
          ...details,
          seats: passengerSeats,
        };
        if (!updated.seats.length) {
          delete updated.seats;
        }
        return updated;
      });
    },
    buildSegmentSeatsChains(seatList) {
      const nextSeatChains = {};
      for (let i = 0; i < seatList.length; i += 1) {
        const pId = seatList[i].passengerId;
        const passengerSeat = seatList[i];

        let chainLeft = passengerSeat.left;
        let chainRight = passengerSeat.right;
        let minColumn = passengerSeat.column;
        let hasAdult = !!this.travelersByAge["ADULT"].passengers[pId];

        const mergedChainIndexes = [];
        const rowChains = nextSeatChains[passengerSeat.row] || [];
        let newChain = [passengerSeat.number];
        for (let j = 0; j < rowChains.length; j += 1) {
          const current = rowChains[j];
          if (current.left && current.left.number === passengerSeat.number) {
            mergedChainIndexes.push(j);
            newChain = [...newChain, ...current.chain];
            chainRight = current.right;
            hasAdult = hasAdult || current.hasAdult;
          }
          if (current.right && current.right.number === passengerSeat.number) {
            mergedChainIndexes.push(j);
            newChain = [...current.chain, ...newChain];
            chainLeft = current.left;
            minColumn = current.minColumn;
            hasAdult = hasAdult || current.hasAdult;
          }
        }
        // Filter merged chains
        nextSeatChains[passengerSeat.row] = [
          ...rowChains.filter((_, i) => !mergedChainIndexes.includes(i)),
          {
            left: chainLeft,
            chain: newChain,
            right: chainRight,
            minColumn,
            hasAdult,
          },
        ];
        nextSeatChains[passengerSeat.row].sort(
          (c1, c2) => c1.minColumn - c2.minColumn,
        );
      }
      return nextSeatChains;
    },
    checkSeatsAreValidForChildren(segmentId, passengerId, seat) {
      try {
        const selectedSeats = this.parsedSeatSelections[segmentId] || {};
        const selected = selectedSeats[passengerId] || {};
        const selectedDistinctRows = {};
        const totalAdults = this.travelersByAge["ADULT"].count;
        let adultsLeftToSeat = totalAdults;

        const seatList = [
          ...Object.entries(selectedSeats)
            .map((entry) => ({ passengerId: entry[0], ...entry[1] }))
            .filter((s) => s.number !== selected.number),
          {
            passengerId,
            ...seat,
          },
        ];
        seatList.forEach((s) => {
          selectedDistinctRows[s.row || 0] = true;
          // If seat is selected by an adult
          if (this.travelersByAge["ADULT"].passengers[s.passengerId]) {
            adultsLeftToSeat -= 1;
          }
        });

        // Prevent children to be seated far apart with not enough adults to be near all of them
        if (Object.keys(selectedDistinctRows).length > totalAdults) {
          return false;
        }

        // Prevent children to be seated in isolated seats where the ones next to them are occupied
        if (this.travelersByAge["CHILD"].passengers[passengerId]) {
          const { left, right } = seat;
          const isLeftSideBlocked = !left || left.isBlocked;
          const isRightSideBlocked = !right || right.isBlocked;

          if (isLeftSideBlocked && isRightSideBlocked) {
            return false;
          }
        }

        const nextSeatChains = this.buildSegmentSeatsChains(seatList);
        const chainsWithNoAdults = Object.values(nextSeatChains)
          .flat()
          .filter((chain) => !chain.hasAdult).length;
        if (chainsWithNoAdults > adultsLeftToSeat) {
          return false;
        }

        this.seatChains[segmentId] = { ...nextSeatChains };
        return true;
      } catch (e) {
        this.log("error checkSeatsAreValidForChildren");
        this.log(e);
        return false;
      }
    },
    checkSeatsWerePricedCorrectly(originalSelections, responseFlightOffer) {
      if (!responseFlightOffer) {
        return false;
      }
      const price = responseFlightOffer.price || {};
      const additionalServices = price.additionalServices || [];
      if (
        additionalServices.findIndex((service) => service.type === "SEATS") < 0
      ) {
        return false;
      }

      return Object.keys(originalSelections).reduce((valid, segmentId) => {
        if (!valid) return valid;
        const responseSegmentSelections =
          this.getSegmentSelectedSeatsFromFlightOffer(
            responseFlightOffer,
            segmentId,
          );
        return (
          Object.keys(responseSegmentSelections).length >=
          Object.keys(originalSelections[segmentId] || {}).length
        );
      }, true);
    },
    handleSeatSelection(segmentId, traveler_id, seat) {
      // Sanity check
      if (!segmentId || !traveler_id || !seat) {
        return false;
      }

      try {
        const { number: seatId } = seat;
        const allSelectedSeats = this.parsedSeatSelections;
        const selectedSeats = allSelectedSeats[segmentId] || {};
        const selected = selectedSeats[traveler_id];

        const passengerOnCurrentSeat = Object.keys(selectedSeats).reduce(
          (found, pId) => {
            if (found) {
              // Already found
              return found;
            }
            const currentSeat = selectedSeats[pId];
            if (currentSeat && currentSeat.number === seatId) {
              return pId;
            }
            return found;
          },
          undefined,
        );

        if (passengerOnCurrentSeat) {
          delete selectedSeats[passengerOnCurrentSeat];
          // Commit all changes
          allSelectedSeats[segmentId] = selectedSeats;
          this.parsedSeatSelections = allSelectedSeats;
          return true;
        }

        // Sanity check for availability
        if (!this.isSeatAvailableForPassenger(traveler_id, seat)) {
          return this.failSelectionWithReason({
            message: "Il posto selezionato non è più disponibile.",
            traveler_id,
            seat,
          });
        }

        if (this.isPassengerHoldingInfant(traveler_id)) {
          const { characteristicsCodes = [] } = seat;
          if (!characteristicsCodes.includes(SEAT_SUITABLE_FOR_INFANT)) {
            return this.failSelectionWithReason({
              message:
                "Questo posto non va bene per chi viaggia con un neonato in braccio. Dai un’occhiata alla legenda e scegli un posto adatto.",
              traveler_id,
              seat,
            });
          }
        }

        // Clear selection for the user on the segment
        if (this.travelersByAge["CHILD"].count) {
          const isValid = this.checkSeatsAreValidForChildren(
            segmentId,
            traveler_id,
            seat,
          );
          if (!isValid) {
            return this.failSelectionWithReason({
              message:
                "Ops! I bambini sotto gli 11 anni non possono sedersi da soli. Mettili vicino a un adulto.",
              traveler_id,
              seat,
            });
          }
        }
        delete selectedSeats[traveler_id];

        // Add new selection
        if (!selected || selected.number !== seatId) {
          selectedSeats[traveler_id] = seat;
        }

        // Commit all changes
        allSelectedSeats[segmentId] = selectedSeats;
        this.parsedSeatSelections = allSelectedSeats;

        this.handle_tracked_event({
          event: "seat-map:selection:select-seat",
          data: {
            type: "info",
            traveler_id,
            seat,
          },
        });

        return true;
      } catch (e) {
        this.handle_tracked_event({
          event: "seat-map:selection:fail",
          data: {
            type: "warning",
            message: e.message,
            traveler_id,
            seat,
          },
        });
        this.log("error handleSeatSelection");
        this.log(e);
        return false;
      }
    },
    handleNextStep() {
      if (this.isEditing) {
        window.eventBus.$emit("seat_map_next_step");
      } else {
        this.handleConfirmSeats();
      }
    },
    updateParentWithCurrentStatus(data) {
      this.isEditing = data.isEditing;
      this.btnText = data.btnText;
      this.btnDisabled = data.btnDisabled;
    },
    hasSeatsSelected() {
      return Object.values(this.parsedSeatSelections).some(
        (segmentSelections) => Object.keys(segmentSelections).length > 0,
      );
    },
    handleConfirmSeats() {
      // If user has not selected any seat just shortcut to checkout
      if (!this.hasSeatsSelected()) {
        this.handle_tracked_event({
          event: "seat-map:submit:no-seats",
          data: {
            type: "info",
          },
        });
        this.$router.push("checkout");
        return;
      }

      this.loading = true;
      this.scroll_top();

      // Update client side flight offer
      const flightOfferWithSeats = this.addSelectedSeatsToFlightOffer(
        this.flight_offer,
        this.parsedSeatSelections,
      );
      this.flight_offer = flightOfferWithSeats;

      const response = service_handler("flightOffersPrice", {
        priceFlightOffersBody: {
          data: {
            type: "flight-offers-pricing",
            flightOffers: [flightOfferWithSeats],
          },
        },
      });
      response
        .then((res) => {
          let areSeatsPricedCorrectly = false;
          try {
            // Realign with server flight offer expecting no real big changes
            if (!res.data || !res.data.data) {
              return;
            }
            const responseFlightOffers = res.data.data.flightOffers;
            if (!responseFlightOffers || !responseFlightOffers.length) {
              return;
            }

            this.flight_offer = responseFlightOffers[0];
            areSeatsPricedCorrectly = this.checkSeatsWerePricedCorrectly(
              this.parsedSeatSelections,
              responseFlightOffers[0],
            );

            // Update booking data on success
            const booking_data = {
              ...this.booking_data,
              live_price: this.get_markup(responseFlightOffers[0]).mu_price,
              flightCreateOrderBody: this.get_flight_order_body({
                flight_offers_pricing: responseFlightOffers,
                traveler_details: this.travelerDetails,
                contact_details: this.contactDetails,
              }),
            };
            this.$store.commit("booking_data", booking_data);
          } catch (e) {
            this.handle_tracked_event({
              event: "seat-map:submit:fail",
              data: {
                type: "error",
                message: e.message,
                reason: "Exception",
              },
            });
            this.log("error handleConfirmSeats");
            this.log(e);
          } finally {
            if (!areSeatsPricedCorrectly) {
              const error_message =
                "Attenzione! Alcuni posti scelti potrebbero non essere più disponibili.";
              this.reloadSeatMap = !this.reloadSeatMap;
              this.handle_tracked_event({
                event: "seat-map:submit:fail",
                data: {
                  type: this.mustSelectSeats ? "error" : "warning",
                  message: error_message,
                  reason: "Missmatch in API Response",
                },
              });
              if (this.mustSelectSeats) {
                this.exitWithGlobalModal(error_message);
                // TODO: se è un must selezionare rimanere sulla pagina
              } else {
                // If we don't actually need this just notify user and go to checkout
                window.eventBus.$emit("got_toast_notification", {
                  text: error_message,
                  variant: "warning",
                });
                this.$router.push("checkout");
              }
            } else {
              this.handle_tracked_event({
                event: "seat-map:submit:success",
                data: {
                  type: "info",
                },
              });
              this.$router.push("checkout");
            }
          }
        })
        .catch((e) => {
          this.handle_tracked_event({
            event: "seat-map:submit:fail",
            data: {
              type: "error",
              message: e.message,
              reason: "Error from APIs",
            },
          });
          this.exitWithGlobalModal(e.message, e.status);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    exitWithGlobalModal(message, callback = function () {}, status = 200) {
      window.eventBus.$emit("got_modal_notification", {
        msg: message,
        status,
        callback,
      });
    },
    handle_tracked_event(params) {
      const { event, data } = params;
      const payload = {
        ...data,
        flightOffer: this.flight_offer_rybbit_data,
        selections: slim_selections(this.parsedSeatSelections),
      };

      if (data.seat) {
        payload.seat = slim_seat(data.seat);
      }
      if (data.traveler_id && this.travelersMap[data.traveler_id]) {
        payload.traveler = slim_traveler(this.travelersMap[data.traveler_id]);
      }
      rybbit_event(event, payload);
    },
  },
};
</script>

<style lang="scss">
.seat-selection-page {
  padding-bottom: $grid-gutter-width;

  .seat-map-frame {
    border: 1px solid var(--light-gray);
    border-radius: $border-radius-lg * 2;

    padding-left: $grid-gutter-width / 2;
    padding-right: $grid-gutter-width / 2;
    padding-bottom: $grid-gutter-width / 2;
    margin-bottom: $grid-gutter-width / 3;
  }

  label {
    font-size: small;
  }

  .credit-warning {
    font-size: $font-size-base;
    line-height: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: $grid-gutter-width / 6;
  }
}
</style>
